import {
    Bar,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
    BarChart,
    ResponsiveContainer,
} from "recharts";

import Typography from "@/ui/atoms/Typography";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { MappingAnalytics } from "@/ui/organisms/dashboardTypes";
import { CHART_COLORS } from "@/ui/organisms/dashboardTypes";
import { createSortByNumberPropertyComparator } from "@/utils/sort";
import Box from "@/ui/atoms/Box";

const compareByValueDesc = createSortByNumberPropertyComparator(
    "value",
    "desc",
);

export const retrieveMappingAnalyticsNodes = (
    mappingAnalytics: MappingAnalytics,
) => {
    const misclassifiedAccounts =
        (mappingAnalytics.alteredMappings / mappingAnalytics.mappingsCount) *
        100;
    const misclassifiedAccountTypePairs =
        mappingAnalytics.misclassifiedAccountTypePairs;
    const mismatchPercentage =
        misclassifiedAccountTypePairs.length > 0
            ? (misclassifiedAccountTypePairs.length /
                  mappingAnalytics.mappingsCount) *
              100
            : 0;

    const getMismatchColor = (percentage: number) => {
        if (percentage === 0) return "#2ecc71"; // green
        if (percentage <= 5) return "#f1c40f"; // yellow
        return "#e74c3c"; // red
    };

    const barChartData = [
        {
            name: "Correct Mappings",
            value:
                mappingAnalytics.mappingsCount -
                mappingAnalytics.alteredMappings,
        },
        {
            name: "Altered Mappings",
            value: mappingAnalytics.alteredMappings,
        },
    ];

    const sourceBarData = Object.entries(mappingAnalytics.mostInaccurateSources)
        .map(([accountCode, rate]) => ({
            name: accountCode,
            value: rate,
        }))
        .toSorted(compareByValueDesc);

    const targetBarData = Object.entries(mappingAnalytics.mostInaccurateTargets)
        .map(([accountCode, rate]) => ({
            name: accountCode,
            value: rate,
        }))
        .toSorted(compareByValueDesc);

    const coaBarData = Object.entries(mappingAnalytics.accuracyByCoa)
        .map(([coaName, accuracy]) => ({
            coa: coaName,
            value: accuracy,
        }))
        .toSorted((a, b) => {
            const aCoa = a.coa.split(" ")[1];
            const bCoa = b.coa.split(" ")[1];
            return (
                (aCoa !== undefined ? parseInt(aCoa) : 0) -
                (bCoa !== undefined ? parseInt(bCoa) : 0)
            );
        });

    return mappingAnalytics.mappingsCount > 0
        ? [
              {
                  element: (
                      <>
                          <Typography variant="h1" className="text-center">
                              Mapping Overview
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <PieChart>
                                  <Tooltip />
                                  <Pie
                                      data={barChartData}
                                      dataKey="value"
                                      nameKey="name"
                                      label={({ name, value }) =>
                                          `${name}: ${value}`
                                      }
                                  >
                                      {barChartData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Pie>
                              </PieChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: { i: "Mapping Overview", w: 3, h: 2, x: 0, y: 4 },
              },
              {
                  element: (
                      <>
                          <Typography variant="h1" className="text-center">
                              Most Inaccurate Source Accounts
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart data={sourceBarData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {sourceBarData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: { i: "Inaccurate Sources", w: 3, h: 2, x: 3, y: 4 },
              },
              {
                  element: (
                      <>
                          <Typography variant="h1" className="text-center">
                              Most Inaccurate Target Accounts
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart data={targetBarData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {targetBarData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: { i: "Inaccurate Targets", w: 3, h: 2, x: 0, y: 4 },
              },
              {
                  element: (
                      <>
                          <Typography variant="h1" className="text-center">
                              Accuracy by COA
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart data={coaBarData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="coa" />
                                  <YAxis domain={[0, 100]} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {coaBarData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: { i: "Accuracy by COA", w: 6, h: 2, x: 0, y: 0 },
              },
              {
                  element: (
                      <>
                          <Typography variant="h1" className="text-center">
                              Misclassified Accounts
                          </Typography>
                          <Box
                              sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "1rem",
                                  height: "100%",
                                  textAlign: "center",
                              }}
                          >
                              <Box
                                  sx={{
                                      display: "grid",
                                      gridTemplateRows: "1fr 1fr 1fr",
                                      height: "100%",
                                      width: "80%",
                                  }}
                              >
                                  <Typography
                                      variant="h2"
                                      sx={{
                                          textAlign: "center",
                                      }}
                                  >
                                      Misclassified Accounts
                                  </Typography>
                                  <Typography
                                      variant="h1"
                                      style={{
                                          fontFamily: "DM Sans",
                                          color: getMismatchColor(
                                              misclassifiedAccounts,
                                          ),
                                          fontSize: "4rem",
                                          height: "max-content",
                                          textAlign: "center",
                                      }}
                                  >
                                      {misclassifiedAccounts.toFixed(2)}%
                                  </Typography>
                                  <Typography
                                      variant="body1"
                                      sx={{
                                          textAlign: "center",
                                      }}
                                  >
                                      ({mappingAnalytics.alteredMappings} of{" "}
                                      {mappingAnalytics.mappingsCount})
                                  </Typography>
                              </Box>
                              <Box
                                  sx={{
                                      display: "grid",
                                      gridTemplateRows: "1fr 1fr 1fr",
                                      height: "100%",
                                      width: "80%",
                                      textAlign: "center",
                                  }}
                              >
                                  <Typography
                                      variant="h2"
                                      sx={{
                                          textAlign: "center",
                                      }}
                                  >
                                      Misclassified Account Types
                                  </Typography>
                                  <Typography
                                      variant="h1"
                                      style={{
                                          fontFamily: "DM Sans",
                                          color: getMismatchColor(
                                              mismatchPercentage,
                                          ),
                                          fontSize: "4rem",
                                          height: "max-content",
                                          textAlign: "center",
                                      }}
                                  >
                                      {mismatchPercentage.toFixed(2)}%
                                  </Typography>
                                  <Typography
                                      variant="body1"
                                      sx={{
                                          textAlign: "center",
                                      }}
                                  >
                                      ({misclassifiedAccountTypePairs.length} of{" "}
                                      {mappingAnalytics.mappingsCount})
                                  </Typography>
                              </Box>
                          </Box>
                      </>
                  ),
                  options: { i: "misclassified Types", w: 3, h: 2, x: 6, y: 0 },
              },
          ]
        : [
              {
                  element: (
                      <LayoutFillParentCentered>
                          <Typography variant="h1">
                              No Mapping Data Found
                          </Typography>
                      </LayoutFillParentCentered>
                  ),
                  options: {
                      i: "No Mappings",
                      w: 100,
                      h: 2,
                      x: 0,
                      y: 0,
                      boxed: false,
                  },
              },
          ];
};
